"use client";
import { trackUserAction } from "@/app/actions/posthog";
import { checkSetupStepComplete, markSetupStepComplete } from "@/app/actions/storeSetup";
import useKlaviyoApi from "@/app/hooks/useKlaviyoApi";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { getCurrentUser } from "@/lib/helper/credentials/getCompanyCredentialsBackend";
import { PostHogEventName } from "@/types/posthog";
import { createClient } from "@/utils/supabase/client";
import { CheckCircleIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "@/lib/helper/ui/toast";
import { Loading } from "../global/Loading";

// Klaviyo OAuth Configuration
const KLAVIYO_CLIENT_ID = process.env.NEXT_PUBLIC_KLAVIYO_OAUTH_CLIENT_ID;
const KLAVIYO_AUTH_URL = "https://www.klaviyo.com/oauth/authorize";
// Ensure no trailing slashes or other characters that could cause mismatch
const REDIRECT_URI = (process.env.NEXT_PUBLIC_FRONTEND_URL + "/oauth/redirect").trim();

// Import scopes from shared constants
import { KLAVIYO_OAUTH_SCOPES } from "@/app/lib/klaviyo/constants";

// Scopes required for the application
const SCOPES = KLAVIYO_OAUTH_SCOPES;
interface KlaviyoOauthProps {
  companyId: string;
  hideButton?: boolean;
  hideText?: boolean;
  // Add new prop to allow parent to override rendering when completed
  hideWhenCompleted?: boolean;
  // URL to redirect to after successful OAuth (defaults to settings page)
  returnUrl?: string;
}

export default function KlaviyoOauth({ companyId, hideButton, hideText, hideWhenCompleted, returnUrl }: KlaviyoOauthProps) {
  const supabase = createClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [accountName, setAccountName] = useState<string | null>(null);

  useEffect(() => {
   
   const  getEmail = async () => {
    const currentUser = await getCurrentUser();
    setEmail(currentUser.email);
   }

    getEmail();

    const checkConnection = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("klaviyo_connections")
          .select("*")
          .eq("company_id", companyId)
          .single();

        if (error && error.code !== "PGRST116") {
          console.error("Error checking Klaviyo connection:", error);
          setError("Failed to check connection status");
        } else {
          setIsConnected(!!data);

          // Only mark step complete if connection exists and step isn't already completed
          if (data) {
            const isStepCompleted = await checkSetupStepComplete("klaviyo", companyId);
            if (!isStepCompleted) {
              markSetupStepComplete("klaviyo", companyId);
            }

            // Fetch Klaviyo account name
            try {
              const accountResponse = await fetch(`/api/klaviyo/account?companyId=${companyId}`);
              if (accountResponse.ok) {
                const accountData = await accountResponse.json();
                if (accountData.data?.[0]?.attributes?.contact_information) {
                  const contactInfo = accountData.data[0].attributes.contact_information;
                  setAccountName(contactInfo.organization_name || contactInfo.default_sender_name || null);
                }
              }
            } catch (err) {
              console.error("Error fetching Klaviyo account name:", err);
            }
          }
        }
      } catch (err) {
        console.error("Error:", err);
        setError("An unexpected error occurred");
      } finally {
        setLoading(false);
      }
    };

    // Only check connection if companyId is a valid UUID
    const isValidCompanyId = companyId && companyId !== "undefined" && companyId.length > 0;
    if (isValidCompanyId) {
      checkConnection();
    }
  }, [companyId, supabase]);

  const initiateOAuth = async () => {
    setLoading(true);
    setError(null);

    await trackUserAction(email, companyId, PostHogEventName.ONBOARDING_CONNECT_KLAVIYO_CLICKED)

    try {
      // Validate client ID is available
      if (!KLAVIYO_CLIENT_ID) {
        throw new Error("Klaviyo Client ID is not configured");
      }
      
      // Clear any previous OAuth state from sessionStorage
      sessionStorage.removeItem("klaviyo_code_verifier");
      sessionStorage.removeItem("klaviyo_oauth_state");
      sessionStorage.removeItem("klaviyo_company_id");
      sessionStorage.removeItem("klaviyo_return_url");
      
      // Generate code verifier and challenge for PKCE
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);

      // Store the code verifier in sessionStorage for later use
      sessionStorage.setItem("klaviyo_code_verifier", codeVerifier);
      sessionStorage.setItem("klaviyo_company_id", companyId);
      if (returnUrl) {
        sessionStorage.setItem("klaviyo_return_url", returnUrl);
        console.log("[KlaviyoOauth] Stored returnUrl in sessionStorage:", returnUrl);
      }

      // Prepare state parameter to prevent CSRF
      const state = generateRandomString(32);
      sessionStorage.setItem("klaviyo_oauth_state", state);

      // Double-check that values were stored properly
      const storedVerifier = sessionStorage.getItem("klaviyo_code_verifier");
      const storedState = sessionStorage.getItem("klaviyo_oauth_state");
      const storedCompanyId = sessionStorage.getItem("klaviyo_company_id");
      
      if (!storedVerifier || !storedState || !storedCompanyId) {
        throw new Error("Failed to store OAuth parameters in session storage");
      }

      // Save debug info for troubleshooting
      const debugData = {
        clientId: KLAVIYO_CLIENT_ID,
        redirectUri: REDIRECT_URI,
        scopes: SCOPES.join(" ")
      };
      setDebugInfo(JSON.stringify(debugData, null, 2));

      // Construct the OAuth URL
      const authUrl = `${KLAVIYO_AUTH_URL}?response_type=code&client_id=${encodeURIComponent(KLAVIYO_CLIENT_ID)}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&scope=${encodeURIComponent(SCOPES.join(" "))}&state=${encodeURIComponent(state)}&code_challenge_method=S256&code_challenge=${encodeURIComponent(codeChallenge)}`;

      // Redirect to Klaviyo's authorization page
      window.location.href = authUrl;
    } catch (err) {
      console.error("Error initiating OAuth:", err);
      setError("Failed to initiate authentication");
      setLoading(false);
    }
  };

  // Use centralized disconnect which revokes tokens in Klaviyo and then removes local connection
  const { disconnectKlaviyo: disconnectKlaviyoApi } = useKlaviyoApi({ companyId });
  const disconnectKlaviyo = async () => {
    setLoading(true);
    setError(null);
    try {
      await disconnectKlaviyoApi();
      setIsConnected(false);
      toast.success("Disconnected Klaviyo successfully");
    } catch (err) {
      console.error("Error disconnecting Klaviyo:", err);
      setError("Failed to disconnect from Klaviyo");
      toast.error("Failed to disconnect from Klaviyo");
    } finally {
      setLoading(false);
    }
  };

  // Generate a random string for code verifier
  const generateRandomString = (length: number): string => {
    const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
    let result = "";
    const randomValues = new Uint8Array(length);
    window.crypto.getRandomValues(randomValues);
    
    for (let i = 0; i < length; i++) {
      result += charset[randomValues[i] % charset.length];
    }
    
    return result;
  };

  // Generate code verifier for PKCE
  const generateCodeVerifier = (): string => {
    return generateRandomString(64);
  };

  // Generate code challenge from verifier
  const generateCodeChallenge = async (verifier: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await window.crypto.subtle.digest("SHA-256", data);
    
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  };






  if (loading) {
    return <Loading />;
  }

  if (hideWhenCompleted && isConnected) {
    return null;
  }

  return (
    <div className="space-y-4">
      
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {debugInfo && !isConnected && (
        <div className="p-4 bg-gray-50">
          <p className="text-sm font-medium mb-2">OAuth Configuration:</p>
          <pre className="text-xs overflow-x-auto whitespace-pre-wrap">
            {debugInfo}
          </pre>
        </div>
      )}

      {isConnected ? (
        <div className="space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-kinetic-light-pink text-kinetic-magenta rounded-full text-sm font-medium">
            <CheckCircleIcon className="w-4 h-4" />
            Connected to Klaviyo
            {accountName && (
              <span className="font-normal opacity-80">({accountName})</span>
            )}
          </div>

          {!hideButton && (
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-500 hover:text-slate-700 hover:bg-slate-100"
              onClick={disconnectKlaviyo}
            >
              Disconnect
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {!hideText && (
            <p className="text-sm text-slate-600">
              Connect your Klaviyo account to create and manage interactive email templates.
            </p>
          )}

          <Button
            onClick={() => initiateOAuth()}
            className="bg-kinetic-light-pink hover:bg-kinetic-light-pink/90 text-kinetic-magenta font-medium px-5 py-2 rounded-lg transition-all"
          >
            Connect Klaviyo Account
          </Button>
        </div>
      )}
    </div>
  );
}
